import { XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import {
  formatSidebarLgWidthRem,
  shellWidthFromTabsStripPx,
  SIDEBAR_LG_WIDTH_CLASS,
  SIDEBAR_LG_WIDTH_MIN,
} from "pages/project/common/masterDetailLayout";
import {
  createContext,
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/ui";

interface Props {
  children: ReactNode;
  title: ReactNode;
  onClose: () => void;
  /** Stable identity of the shown detail — opacity crossfade when it changes; width only on open/close. */
  contentKey: string;
}

const SideBarTabsWidthContext = createContext<(widthPx: number | null) => void>(() => {});

/** Used by SideBarTabsList to drive shell width from the tab strip (not tab content). */
export function useSideBarTabsWidthReporter() {
  return useContext(SideBarTabsWidthContext);
}

/**
 * Keeps AnimatePresence mounted so the SideBar width exit can run when `present` becomes false.
 * Use a stable `key` on the SideBar child (not the entity id) so item switches only crossfade content.
 */
export function SideBarPresence({ present, children }: { present: boolean; children: ReactNode }) {
  return <AnimatePresence>{present ? children : null}</AnimatePresence>;
}

export const SideBar = forwardRef<HTMLDivElement, Props>(function SideBar(
  { children, title, onClose, contentKey },
  ref
) {
  const { t } = useTranslation();
  const localRef = useRef<HTMLDivElement | null>(null);
  const [tabsStripWidthPx, setTabsStripWidthPx] = useState<number | null>(null);

  const reportTabsWidthPx = useCallback((widthPx: number | null) => {
    setTabsStripWidthPx((prev) => (prev === widthPx ? prev : widthPx));
  }, []);

  const lgWidth = useMemo(() => {
    if (tabsStripWidthPx == null) {
      return SIDEBAR_LG_WIDTH_MIN;
    }
    return formatSidebarLgWidthRem(shellWidthFromTabsStripPx(tabsStripWidthPx));
  }, [tabsStripWidthPx]);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  // After open, drop Framer's inline width so CSS owns steady state (`max-lg:w-full` / CSS var width).
  const clearInlineWidthAfterOpen = useCallback(() => {
    const el = localRef.current;
    if (el?.style.width && el.style.width !== "0px") {
      el.style.width = "";
    }
  }, []);

  const shellStyle = {
    ["--sidebar-lg-width" as string]: lgWidth,
  } as CSSProperties;

  const shellMotion = {
    initial: { width: 0 },
    animate: { width: lgWidth },
    exit: { width: 0 },
    transition: { duration: 0.18, ease: [0.4, 0, 0.2, 1] as const },
  };

  const contentMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.12, ease: "easeInOut" as const },
  };

  return (
    <SideBarTabsWidthContext.Provider value={reportTabsWidthPx}>
      <motion.div
        ref={setRefs}
        data-test="sidebar-panel"
        data-sidebar-lg-width={lgWidth}
        style={shellStyle}
        className={cn(
          "max-lg:w-full max-lg:overflow-hidden lg:my-4 lg:min-w-0 lg:max-w-full lg:shrink-0 lg:overflow-hidden",
          SIDEBAR_LG_WIDTH_CLASS
        )}
        {...shellMotion}
        onAnimationComplete={clearInlineWidthAfterOpen}
      >
        {/* w-full + min-w-0: fill the shell; tab/HTML content must not expand via Framer "auto" */}
        <div className="text-color-base bg-color-base h-full w-full min-w-0 overflow-hidden rounded border border-slate-200 dark:border-slate-800">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={contentKey}
              {...contentMotion}
              className="sticky top-0 flex min-w-0 flex-col overflow-x-hidden px-4 py-6 sm:px-6"
            >
              <div className="mb-4 flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-col flex-wrap space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
                  {title}
                </div>
                <div className="ml-3 flex h-7 shrink-0 items-center">
                  <button
                    type="button"
                    onClick={onClose}
                    data-test="sidebar-close-panel"
                    className="offset-ring-500 rounded-md text-slate-400 outline-0 hover:text-slate-500 dark:hover:text-slate-50"
                  >
                    <span className="sr-only">{t("task-base.sidebar.closePanel")}</span>
                    <XMarkIcon className="size-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </SideBarTabsWidthContext.Provider>
  );
});
